"use client";

/**
 * NavDropdown — เมนู dropdown สำหรับ top nav
 * ใช้จัดกลุ่มลิงก์ที่เกี่ยวข้องกัน (เช่น หลักสูตร, ทวนสอบ, รายงาน)
 * แทนการเรียงลิงก์ยาวเป็นแถวเดียว
 */

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export interface NavDropdownItem {
  href: string;
  label: string;
  icon?: string;
}

export function NavDropdown({
  label,
  icon,
  items,
}: {
  label: string;
  icon?: string;
  items: NavDropdownItem[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 hover:underline text-sm"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {icon && <span>{icon}</span>} {label}
        <span className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 min-w-[200px] rounded-lg border bg-white shadow-lg py-1 z-20"
        >
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm text-slate-700 hover:bg-brand-50 hover:text-brand-700"
            >
              {item.icon && <span className="mr-1.5">{item.icon}</span>}
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
